// Pure logic for folding a `table` viz_type card by default (27 of 116
// charts in the real corpus are tables, and a handful group 4+ dimensions
// with several metrics — a table renders every row flat inside its card, so
// a few of those turn a dashboard into a vertical scroll marathon and bury
// whatever is below them). Nothing here touches React: `chart-card.tsx`
// reads these to decide the collapsed/expanded copy and which row slice to
// render; the fold/unfold toggle itself is local component state so it never
// triggers a refetch or disturbs `useTableSort`.

/** A table at or below this many rows reads fine fully expanded — collapsing
 *  it would be pure friction (a 3-row table hidden behind a click teaches the
 *  reader nothing a flat table wouldn't). Chosen so it comfortably covers a
 *  small breakdown table (a handful of categories) while still catching the
 *  wide/tall outliers this feature exists for, like "Seller 360, activity"
 *  (4 group-by dimensions x 5 metrics, well over 20 rows in practice). */
export const FOLD_ROW_THRESHOLD = 8;

/** How many rows a COLLAPSED table shows. A collapsed card must still be
 *  informative on its own — the reader should learn the shape of the data
 *  (columns, and a real peek at the first few rows) without expanding, not
 *  just a row/column count. 5 is enough to show that peek while keeping the
 *  collapsed card roughly the height of a "lg" chart skeleton (220px), so it
 *  doesn't dominate the section the way the full table would. */
export const PREVIEW_ROW_COUNT = 5;

/** Whether this table offers a fold control and, by the same test, whether it
 *  STARTS collapsed. A fullscreen/expanded presentation should never start
 *  collapsed; that override belongs to the CALLER, not a mode argument here. */
export function isFoldable(rowCount: number): boolean {
  return rowCount > FOLD_ROW_THRESHOLD;
}

/** Rows visible while collapsed: `PREVIEW_ROW_COUNT`, capped at the actual
 *  row count so a short table never appears to promise more preview rows
 *  than it has. */
export function previewRowCount(rowCount: number): number {
  return Math.min(PREVIEW_ROW_COUNT, rowCount);
}

/** "9 columns, 137 rows" — the informative summary a collapsed table shows
 *  in place of the rows it isn't rendering, so the card still teaches the
 *  reader something without expanding. */
export function tableSummaryLabel(rowCount: number, columnCount: number): string {
  const rows = rowCount === 1 ? "1 row" : `${rowCount.toLocaleString()} rows`;
  const cols = columnCount === 1 ? "1 column" : `${columnCount.toLocaleString()} columns`;
  return `${cols}, ${rows}`;
}

/** Button label when collapsed: says what will happen and to what, per the
 *  design bar ("Show all 412 rows" beats "Expand"). */
export function expandButtonLabel(rowCount: number): string {
  return `Show all ${rowCount.toLocaleString()} rows`;
}

/** Button label when expanded. Symmetric with `expandButtonLabel`: names the
 *  action and its target rather than a bare "Collapse". */
export function collapseButtonLabel(rowCount: number): string {
  return `Collapse to ${previewRowCount(rowCount)} rows`;
}

/** The note rendered above the table, or `null` when neither the fold nor
 *  the server's own row cap has anything to disclose. Composes with the
 *  pre-existing "server_pagination" note rather than duplicating or
 *  contradicting it: `rowCount` here is always the count of rows the SERVER
 *  handed back (already capped at `row_limit` when `serverPagination` is
 *  true), so a collapsed+paginated table gets one merged sentence naming
 *  both limits instead of two notes that look like they disagree. */
export function foldNote(params: {
  collapsed: boolean;
  rowCount: number;
  serverPagination: boolean;
}): string | null {
  const { collapsed, rowCount, serverPagination } = params;
  const total = rowCount.toLocaleString();
  if (collapsed) {
    const shown = previewRowCount(rowCount).toLocaleString();
    return serverPagination
      ? `Showing ${shown} of the ${total} rows loaded here. Paging past ${total} isn't built yet.`
      : `Showing the first ${shown} of ${total} rows.`;
  }
  return serverPagination
    ? `Showing the first ${total} rows. Paging past this limit isn't built yet.`
    : null;
}
